import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  Button, CircularProgress, Alert, Chip, Grid, Card, CardContent, TextField,
  FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, IconButton, LinearProgress, Tooltip
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VerifiedIcon from '@mui/icons-material/Verified';
import SearchIcon from '@mui/icons-material/Search';
import { ingestionService } from '../services/ingestionService';
import { sourceSystemService } from '../services/sourceSystemService';

const FILE_STATUSES = [
  'registered', 'schema_detected', 'mapping_suggested', 'mapped',
  'quality_assessed', 'validating', 'validated', 'validation_failed',
  'submitted_to_dve', 'correction_requested'
];

const statusColors = {
  registered: 'default', schema_detected: 'info', mapping_suggested: 'info',
  mapped: 'primary', quality_assessed: 'primary', validating: 'warning',
  validated: 'success', validation_failed: 'error',
  submitted_to_dve: 'success', correction_requested: 'error'
};

function IngestionDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [files, setFiles] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [sourceSystems, setSourceSystems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [filters, setFilters] = useState({ status: '', source_system_id: '', file_type: '', search: '' });

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSourceSystem, setUploadSourceSystem] = useState('');

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [sampleData, setSampleData] = useState(null);
  const [custodyLog, setCustodyLog] = useState([]);
  const [detailTab, setDetailTab] = useState(0);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [systemsData, statsData] = await Promise.all([
        sourceSystemService.list(),
        ingestionService.getStatistics()
      ]);
      setSourceSystems(Array.isArray(systemsData) ? systemsData : systemsData.systems || []);
      setStats(statsData ?? {});
      await loadFiles();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadFiles = async () => {
    try {
      const data = await ingestionService.listFiles(filters);
      setFiles(data.files || []);
      setTotalFiles(data.total || 0);
    } catch (err) {
      console.error('Failed to load files:', err);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    try {
      setUploading(true);
      const result = await ingestionService.uploadFile(uploadFile, {
        source_system_id: uploadSourceSystem || undefined
      });
      if (result.duplicate) {
        setSnackbar({ open: true, message: result.message, severity: 'warning' });
      } else {
        setSnackbar({ open: true, message: `File uploaded: ${uploadFile.name}`, severity: 'success' });
      }
      setUploadOpen(false);
      setUploadFile(null);
      setUploadSourceSystem('');
      loadData();
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Upload failed', severity: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleViewDetail = async (file) => {
    try {
      const [detail, custody] = await Promise.all([
        ingestionService.getFile(file.file_id),
        ingestionService.getCustody(file.file_id)
      ]);
      setSelectedFile(detail);
      setCustodyLog(custody ?? []);
      setSampleData(null);
      setDetailTab(0);
      setDetailOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load file details', severity: 'error' });
    }
  };

  const handleLoadSample = async () => {
    if (!selectedFile) return;
    try {
      const data = await ingestionService.getSample(selectedFile.file_id);
      setSampleData(data);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load sample data', severity: 'error' });
    }
  };

  const handleVerifyIntegrity = async (fileId) => {
    try {
      const result = await ingestionService.verifyIntegrity(fileId);
      setSnackbar({
        open: true,
        message: result.verified ? 'File integrity verified' : 'Integrity check FAILED - file may have been modified',
        severity: result.verified ? 'success' : 'error'
      });
    } catch (err) {
      setSnackbar({ open: true, message: 'Integrity check failed', severity: 'error' });
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Data Ingestion</Typography>
          <Typography variant="body1" color="text.secondary">
            Upload, track, and manage data files for the AI-native mapping pipeline
          </Typography>
        </Box>
        <Box>
          <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setUploadOpen(true)} sx={{ mr: 1 }}>
            Upload File
          </Button>
          <IconButton onClick={loadData}><RefreshIcon /></IconButton>
        </Box>
      </Box>

      {/* Summary Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Files</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{stats.total || 0}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">ELM is SoR</Typography>
              <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>{stats.by_sor?.elm || 0}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">External (Normalise)</Typography>
              <Typography variant="h5" color="secondary" sx={{ fontWeight: 600 }}>{stats.by_sor?.external || 0}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Size</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{formatBytes(stats.total_size_bytes)}</Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="All Files" />
          <Tab label="By Status" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" label="Search" placeholder="File name..."
              value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filters.status} label="Status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <MenuItem value="">All</MenuItem>
                {FILE_STATUSES.map(s => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Source System</InputLabel>
              <Select value={filters.source_system_id} label="Source System"
                onChange={(e) => setFilters({ ...filters, source_system_id: e.target.value })}>
                <MenuItem value="">All</MenuItem>
                {sourceSystems.map(s => <MenuItem key={s.source_system_id} value={s.source_system_id}>{s.system_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>File Type</InputLabel>
              <Select value={filters.file_type} label="File Type"
                onChange={(e) => setFilters({ ...filters, file_type: e.target.value })}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
                <MenuItem value="xlsx">XLSX</MenuItem>
                <MenuItem value="parquet">Parquet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* File List */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Files ({totalFiles})</Typography>
        {files.length === 0 ? (
          <Alert severity="info">No files found. Upload a file to get started.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>SoR</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Quality</TableCell>
                <TableCell>Uploaded</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.file_id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {file.original_name || file.file_name}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip label={file.file_type} size="small" variant="outlined" /></TableCell>
                  <TableCell>{formatBytes(file.file_size)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 100 }}>
                      {file.sourceSystem?.system_name || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={file.elm_is_system_of_record ? 'ELM' : 'External'} size="small"
                      color={file.elm_is_system_of_record ? 'primary' : 'default'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={file.status.replace(/_/g, ' ')} size="small"
                      color={statusColors[file.status] || 'default'} />
                  </TableCell>
                  <TableCell>
                    {file.quality_score != null ? (
                      <Tooltip title={file.quality_classification?.replace(/_/g, ' ')}>
                        <Chip label={`${(Number(file.quality_score) * 100).toFixed(1)}%`} size="small"
                          color={file.quality_classification === 'auto_accept' ? 'success' :
                            file.quality_classification === 'auto_reject' ? 'error' : 'warning'} />
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleViewDetail(file)}><VisibilityIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleVerifyIntegrity(file.file_id)}>
                      <VerifiedIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Data File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" fullWidth sx={{ py: 3, border: '2px dashed', borderColor: 'primary.main' }}>
              {uploadFile ? uploadFile.name : 'Select file (CSV, JSON, XLSX, Parquet)'}
              <input type="file" hidden accept=".csv,.json,.xlsx,.xls,.parquet,.pq"
                onChange={(e) => setUploadFile(e.target.files[0])} />
            </Button>
            {uploadFile && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {formatBytes(uploadFile.size)} - {uploadFile.type || 'unknown type'}
              </Typography>
            )}
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Source System (optional)</InputLabel>
              <Select value={uploadSourceSystem} label="Source System (optional)"
                onChange={(e) => setUploadSourceSystem(e.target.value)}>
                <MenuItem value="">None (Manual Upload)</MenuItem>
                {sourceSystems.map(s =>
                  <MenuItem key={s.source_system_id} value={s.source_system_id}>{s.system_name} ({s.system_type})</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>
          {uploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        {selectedFile && (
          <>
            <DialogTitle>
              {selectedFile.original_name || selectedFile.file_name}
              <Chip label={selectedFile.status.replace(/_/g, ' ')} size="small" sx={{ ml: 1 }}
                color={statusColors[selectedFile.status] || 'default'} />
            </DialogTitle>
            <DialogContent>
              <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ mb: 2 }}>
                <Tab label="Details" />
                <Tab label="Sample Data" />
                <Tab label="Chain of Custody" />
              </Tabs>

              {detailTab === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">File ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{selectedFile.file_id}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">File Type</Typography>
                    <Typography variant="body2">{selectedFile.file_type?.toUpperCase()}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">Size</Typography>
                    <Typography variant="body2">{formatBytes(selectedFile.file_size)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">SHA-256</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                      {selectedFile.sha256_hash}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">System of Record</Typography>
                    <Chip label={selectedFile.elm_is_system_of_record ? 'ELM (Authoritative)' : 'External (Normalise)'}
                      size="small" color={selectedFile.elm_is_system_of_record ? 'primary' : 'default'} />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="subtitle2" color="text.secondary">Quality Score</Typography>
                    {selectedFile.quality_score != null ? (
                      <Typography variant="body2">{(Number(selectedFile.quality_score) * 100).toFixed(1)}% ({selectedFile.quality_classification?.replace(/_/g, ' ')})</Typography>
                    ) : <Typography variant="body2" color="text.secondary">Not assessed</Typography>}
                  </Grid>
                  {selectedFile.detected_schema && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Detected Schema</Typography>
                      <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
                        <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', m: 0 }}>
                          {JSON.stringify(selectedFile.detected_schema, null, 2)}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}

              {detailTab === 1 && (
                <Box>
                  {!sampleData ? (
                    <Box textAlign="center" py={3}>
                      <Button variant="outlined" onClick={handleLoadSample}>Load Sample Data</Button>
                    </Box>
                  ) : (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        {sampleData.total_sample} sample rows, {sampleData.headers?.length} columns
                      </Typography>
                      <Box sx={{ overflow: 'auto', maxHeight: 400 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              {sampleData.headers?.map(h => <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>)}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sampleData.rows?.slice(0, 10).map((row, i) => (
                              <TableRow key={i}>
                                {sampleData.headers?.map(h => (
                                  <TableCell key={h}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                      {row[h] != null ? String(row[h]) : ''}
                                    </Typography>
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </>
                  )}
                </Box>
              )}

              {detailTab === 2 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Event</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>Context</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {custodyLog.map((entry, i) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(entry.created_at).toLocaleString()}</TableCell>
                        <TableCell><Chip label={entry.event_type} size="small" variant="outlined" /></TableCell>
                        <TableCell>{entry.actor_type || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {JSON.stringify(entry.context)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default IngestionDashboard;
